import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProgressBar from './components/ProgressBar';
import StepOne from './components/StepOne';
import StepTwo from './components/StepTwo';
import ActionButton from './components/ActionButton';
import SuccessScreen from './components/SuccessScreen';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiArrowLeft, FiSend, FiRefreshCw, FiTrash2 } = FiIcons;

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [hasRestoredData, setHasRestoredData] = useState(false);
  const [showDataManager, setShowDataManager] = useState(false);
  const totalSteps = 2;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    pincode: '',
    mapUrl: ''
  });

  const [errors, setErrors] = useState({});

  // Enhanced local storage keys with versioning
  const STORAGE_KEYS = {
    FORM_DATA: 'tiffin_delivery_persistent_data_v2',
    CURRENT_STEP: 'tiffin_delivery_current_step_v2',
    LAST_UPDATED: 'tiffin_delivery_last_updated_v2',
    SUBMISSION_HISTORY: 'tiffin_delivery_submissions_v2',
    USER_PREFERENCES: 'tiffin_delivery_preferences_v2'
  };

  // Enhanced data persistence - Load data from localStorage on component mount
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const savedFormData = localStorage.getItem(STORAGE_KEYS.FORM_DATA);
        const savedStep = localStorage.getItem(STORAGE_KEYS.CURRENT_STEP);
        const lastUpdated = localStorage.getItem(STORAGE_KEYS.LAST_UPDATED);
        const userPreferences = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES);

        // Load user preferences
        if (userPreferences) {
          const prefs = JSON.parse(userPreferences);
          if (prefs.keepDataAfterSubmission !== false) {
            // Default behavior is to keep data
          }
        }

        if (savedFormData) {
          const parsedData = JSON.parse(savedFormData);
          
          // Always restore data regardless of age - maximum stickiness
          setFormData(parsedData);
          
          // Restore step if it's valid
          if (savedStep) {
            const step = parseInt(savedStep);
            if (step >= 1 && step <= totalSteps) {
              setCurrentStep(step);
            }
          }
          
          setHasRestoredData(true);
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
        // Don't clear data on error - preserve what we can
      }
    };

    loadSavedData();
  }, []);

  // Enhanced auto-save - Save data to localStorage whenever formData changes
  useEffect(() => {
    // Always save data, even empty fields for maximum persistence
    try {
      localStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(formData));
      localStorage.setItem(STORAGE_KEYS.LAST_UPDATED, Date.now().toString());
      
      // Save submission count and history for analytics
      const currentHistory = JSON.parse(localStorage.getItem(STORAGE_KEYS.SUBMISSION_HISTORY) || '[]');
      
      // Mark that data has been modified
      if (Object.values(formData).some(value => value.trim() !== '')) {
        setHasRestoredData(true);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }, [formData]);

  // Save current step to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CURRENT_STEP, currentStep.toString());
    } catch (error) {
      console.error('Error saving current step:', error);
    }
  }, [currentStep]);

  // Enhanced clear function - only clears when explicitly requested
  const clearAllSavedData = () => {
    try {
      // Clear all form-related data
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Reset state
      setFormData({
        name: '',
        email: '',
        whatsapp: '',
        pincode: '',
        mapUrl: ''
      });
      setCurrentStep(1);
      setErrors({});
      setHasRestoredData(false);
      setShowDataManager(false);
      setIsSubmitted(false);
      setSubmitError('');
      
      console.log('All saved data cleared successfully');
    } catch (error) {
      console.error('Error clearing saved data:', error);
    }
  };

  // Smart data restore function
  const restorePreviousData = () => {
    const savedData = localStorage.getItem(STORAGE_KEYS.FORM_DATA);
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setFormData(parsedData);
      setHasRestoredData(true);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Auto-save individual field changes
    setTimeout(() => {
      try {
        const currentData = JSON.parse(localStorage.getItem(STORAGE_KEYS.FORM_DATA) || '{}');
        const updatedData = { ...currentData, [field]: value };
        localStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(updatedData));
      } catch (error) {
        console.error('Error auto-saving field:', error);
      }
    }, 100);
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }

      if (!formData.whatsapp.trim()) {
        newErrors.whatsapp = 'WhatsApp number is required';
      } else if (!/^\+?[\d\s-()]{10,}$/.test(formData.whatsapp)) {
        newErrors.whatsapp = 'Please enter a valid WhatsApp number';
      }

      if (!formData.pincode.trim()) {
        newErrors.pincode = 'PIN code is required';
      } else if (!/^\d{6}$/.test(formData.pincode)) {
        newErrors.pincode = 'PIN code must be 6 digits';
      }
    }

    if (step === 2) {
      if (!formData.mapUrl.trim()) {
        newErrors.mapUrl = 'Google Maps URL is required';
      } else if (!formData.mapUrl.includes('maps.google') && !formData.mapUrl.includes('goo.gl') && !formData.mapUrl.includes('maps.app.goo.gl')) {
        newErrors.mapUrl = 'Please enter a valid Google Maps URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
  };

  const submitToWebhook = async (data) => {
    const webhookUrl = 'https://automator.maitreyalabs.com/webhook/7338face-6a77-4015-a718-8ab94682298f';
    
    const payload = {
      name: data.name,
      email: data.email,
      whatsapp: data.whatsapp,
      pincode: data.pincode,
      mapUrl: data.mapUrl,
      submittedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      timestamp: Date.now(),
      source: 'tiffin-delivery-form'
    };

    // Method 1: Try direct fetch with different configurations
    const fetchConfigs = [
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(payload)
      },
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify(payload)
      },
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        mode: 'no-cors',
        body: new URLSearchParams(payload).toString()
      }
    ];

    for (let i = 0; i < fetchConfigs.length; i++) {
      try {
        console.log(`Attempting submission method ${i + 1}...`);
        const response = await fetch(webhookUrl, fetchConfigs[i]);
        
        // For no-cors mode, we can't read the response, but if no error is thrown, assume success
        if (fetchConfigs[i].mode === 'no-cors') {
          console.log('No-cors submission completed');
          return { success: true, method: `no-cors-${i + 1}` };
        }
        
        if (response.ok) {
          const result = await response.json();
          console.log('Webhook response:', result);
          return { success: true, data: result, method: i + 1 };
        }
      } catch (error) {
        console.log(`Method ${i + 1} failed:`, error.message);
        continue;
      }
    }

    // Method 2: Try using a form submission approach
    try {
      console.log('Attempting form submission method...');
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = webhookUrl;
      form.target = '_blank';
      form.style.display = 'none';

      Object.keys(payload).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = payload[key];
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      return { success: true, method: 'form-submission' };
    } catch (error) {
      console.error('Form submission failed:', error);
    }

    // Method 3: Try using navigator.sendBeacon (if available)
    if ('sendBeacon' in navigator) {
      try {
        console.log('Attempting beacon method...');
        const beaconData = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const sent = navigator.sendBeacon(webhookUrl, beaconData);
        if (sent) {
          return { success: true, method: 'beacon' };
        }
      } catch (error) {
        console.error('Beacon method failed:', error);
      }
    }

    // If all methods fail, return error
    return { 
      success: false, 
      error: 'Unable to submit form due to network restrictions. Please try again or contact support.' 
    };
  };

  const handleSubmit = async () => {
    if (validateStep(2)) {
      setIsLoading(true);
      setSubmitError('');
      
      try {
        // Store submission in history (keep data persistent)
        const submissionHistory = JSON.parse(localStorage.getItem(STORAGE_KEYS.SUBMISSION_HISTORY) || '[]');
        const newSubmission = {
          ...formData,
          submittedAt: new Date().toISOString(),
          id: Date.now()
        };
        submissionHistory.push(newSubmission);
        localStorage.setItem(STORAGE_KEYS.SUBMISSION_HISTORY, JSON.stringify(submissionHistory));

        const result = await submitToWebhook(formData);
        
        if (result.success) {
          console.log('Form submitted successfully:', formData);
          console.log('Submission method:', result.method);
          setIsSubmitted(true);
          
          // IMPORTANT: Don't clear saved data after successful submission
          // Keep all form data persistent for maximum stickiness
          
        } else {
          setSubmitError(result.error);
        }
      } catch (error) {
        console.error('Submission error:', error);
        setSubmitError('Network error occurred. Your data has been saved locally. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Modified to not clear data - just reset UI state
  const handleStartOver = () => {
    setCurrentStep(1);
    setIsSubmitted(false);
    setSubmitError('');
    setErrors({});
    
    // Keep form data persistent - don't clear it
    // User data should remain sticky across all actions
    
    // Restore data if it exists
    restorePreviousData();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <StepOne formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 2:
        return <StepTwo formData={formData} updateFormData={updateFormData} errors={errors} />;
      default:
        return null;
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-cream via-brand-white to-brand-cream p-4">
        <div className="max-w-md mx-auto pt-8">
          <SuccessScreen formData={formData} onStartOver={handleStartOver} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-cream via-brand-white to-brand-cream p-4">
      <div className="max-w-md mx-auto pt-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-brand-white rounded-3xl shadow-2xl p-6 mb-6"
        >
          {/* Header */}
          <div className="text-center mb-6">
            <motion.h1
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 10 }}
              className="text-3xl font-bold text-brand-primary mb-2"
            >
              Tiffin Delivery 🍛
            </motion.h1>
            <p className="text-brand-primary/70">Fresh homemade meals delivered daily</p>
          </div>

          {/* Data Management Panel - Only show when explicitly requested */}
          {showDataManager && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-4"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-700">📊 Data Management</h4>
                <button
                  onClick={() => setShowDataManager(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Data Status:</span>
                  <span className="text-green-600 font-medium">✅ Persistent</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="text-gray-700">
                    {localStorage.getItem(STORAGE_KEYS.LAST_UPDATED) 
                      ? new Date(parseInt(localStorage.getItem(STORAGE_KEYS.LAST_UPDATED))).toLocaleString()
                      : 'Never'
                    }
                  </span>
                </div>
                
                <div className="pt-3 border-t border-gray-200">
                  <button
                    onClick={clearAllSavedData}
                    className="w-full py-2 px-4 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiTrash2} className="text-sm" />
                    <span>Clear All Saved Data</span>
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    ⚠️ This will permanently delete all your saved information
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />

          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>

          {/* Error Message */}
          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border-2 border-red-200 text-red-700 p-4 rounded-2xl mb-4"
            >
              <div className="flex items-center space-x-2">
                <span className="text-red-500">⚠️</span>
                <p className="font-medium">Submission Issue</p>
              </div>
              <p className="text-sm mt-1">{submitError}</p>
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col space-y-4 mt-8">
            {currentStep === totalSteps ? (
              <ActionButton
                onClick={handleSubmit}
                icon={FiSend}
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Submitting...' : 'Complete Registration'}
              </ActionButton>
            ) : (
              <ActionButton
                onClick={handleNext}
                icon={FiArrowRight}
              >
                Continue
              </ActionButton>
            )}

            {currentStep > 1 && (
              <ActionButton
                onClick={handlePrev}
                variant="secondary"
                icon={FiArrowLeft}
              >
                Go Back
              </ActionButton>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default App;